import { useMemo, useState } from "react";

import { Popover } from "../ui/Popover";
import { cn } from "../ui/cn";
import { SmileyIcon } from "../ui/icons";
import { EMOJI_GROUPS, EMOJI_KEYWORDS } from "./emoji-data";

/**
 * The full emoji catalogue, browsable by category and searchable by keyword.
 *
 * Sized like the other composer menus rather than a dialog: wide enough for a
 * comfortable grid, capped in height so the reply underneath stays visible.
 * Recently used sits at the top because a support desk reuses the same handful
 * constantly, and scrolling to a tick every time is the thing that makes people
 * stop using a picker at all.
 */

const RECENT_KEY = "helpdesk.recentEmoji";
const RECENT_LIMIT = 18;

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((e) => typeof e === "string") : [];
  } catch {
    return [];
  }
}

function saveRecent(list: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    // A picker preference is never worth breaking the composer over.
  }
}

export function EmojiPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState(EMOJI_GROUPS[0]!.id);
  const [query, setQuery] = useState("");
  const [recent, setRecent] = useState<string[]>(loadRecent);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return null;

    return Object.entries(EMOJI_KEYWORDS)
      .filter(([, keywords]) => keywords.includes(needle))
      .map(([emoji]) => emoji);
  }, [query]);

  const active = EMOJI_GROUPS.find((entry) => entry.id === group) ?? EMOJI_GROUPS[0]!;

  function choose(emoji: string): void {
    onPick(emoji);

    const next = [emoji, ...recent.filter((e) => e !== emoji)].slice(0, RECENT_LIMIT);
    setRecent(next);
    saveRecent(next);

    // Left open on purpose: inserting two or three in a row is common, and
    // reopening the menu for each is more friction than closing it once.
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
      label="Insert emoji"
      trigger={
        <span
          title="Insert emoji"
          className={cn(
            "flex h-7 w-7 items-center justify-center rounded text-muted",
            "hover:bg-surface hover:text-brand-600 dark:hover:text-brand-400",
            open && "bg-surface text-content",
          )}
        >
          <SmileyIcon />
        </span>
      }
    >
      <div className="w-[19rem]">
        <div className="border-b border-line p-2">
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search emoji…"
            className={cn(
              "w-full rounded border border-line bg-surface px-2 py-1 text-xs text-content",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            )}
          />
        </div>

        {/* Category tabs, hidden while searching -- results span every group. */}
        {!results ? (
          <div
            role="tablist"
            aria-label="Emoji categories"
            className="flex items-center gap-0.5 border-b border-line px-1.5 py-1"
          >
            {EMOJI_GROUPS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-selected={entry.id === group}
                title={entry.label}
                aria-label={entry.label}
                onClick={() => setGroup(entry.id)}
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded text-sm",
                  entry.id === group ? "bg-surface-2" : "opacity-60 hover:opacity-100",
                )}
              >
                {entry.icon}
              </button>
            ))}
          </div>
        ) : null}

        <div className="max-h-56 overflow-y-auto p-2">
          {results ? (
            results.length === 0 ? (
              <p className="py-6 text-center text-xs text-muted">
                No matches. Try browsing by category.
              </p>
            ) : (
              <Grid emoji={results} onPick={choose} />
            )
          ) : (
            <>
              {recent.length > 0 ? (
                <Section label="Recently used">
                  <Grid emoji={recent} onPick={choose} />
                </Section>
              ) : null}

              <Section label={active.label}>
                <Grid emoji={active.emoji} onPick={choose} />
              </Section>
            </>
          )}
        </div>
      </div>
    </Popover>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="mb-1 px-0.5 text-[10px] font-medium uppercase tracking-wide text-faint">
        {label}
      </div>
      {children}
    </div>
  );
}

function Grid({
  emoji,
  onPick,
}: {
  emoji: string[];
  onPick: (emoji: string) => void;
}) {
  return (
    <div className="grid grid-cols-9 gap-0.5">
      {emoji.map((glyph, index) => (
        <button
          // Ranges can repeat a glyph across groups, so the index is part of
          // the key -- a duplicate key would drop buttons silently.
          key={`${glyph}-${index}`}
          type="button"
          onClick={() => onPick(glyph)}
          aria-label={`Insert ${glyph}`}
          className="flex h-7 w-7 items-center justify-center rounded text-lg leading-none hover:bg-surface-2"
        >
          {glyph}
        </button>
      ))}
    </div>
  );
}
